<script>
import {defineComponent, reactive} from "vue";
import Icon from "@/javascript/views/components/Icon.vue";
import {useDonationSessionStore} from "../../../stores/donationSession";
import {useAnalyticEventStore} from "../../../stores/analyticEvents";
import {useLandingPageStore} from "../../../stores/landingPage";
import {ApplicationHelper} from "../../../helpers/ApplicationHelper";
import BaseModal from "../BaseModal.vue";
import {useUserStore} from "../../../stores/user";

export default defineComponent({
  components: {BaseModal, Icon},
  props: {
    layout: {
      type: String,
      default: 'contact',
      validator: (value) => ['basic', 'contact'].includes(value)
    },
    backgroundColor: {
      type: String,
      default: '#FFFFFF'
    },
    primaryColorDark: {
      type: String,
      default: '#05421a'
    },
    textColor: {
      type: String,
      default: ''
    },
    logoSrc: {
      type: String,
      default: ''
    },
    logoAlt: {
      type: String,
      default: ''
    },
    logoMaxHeight: {
      type: String,
      default: "40px"
    },
    organization: {
      type: Object,
      required: true
    },
    disableNavigation: {
      type: Boolean,
      default: false
    },
    copyrightText: {
      type: String,
      default: ""
    },
    version: {
      type: String,
      default: "1.0"
    },
    contactCopy: {
      type: String,
      default: ''
    },
    contactButtonText: {
      type: String,
      default: 'Contact Us'
    },
    contactButtonHideChevron: {
      type: Boolean,
      default: false
    },
    contactButtonBackgroundColor: {
      type: String,
      default: '#085E2DFF'
    },
    contactButtonTextColor: {
      type: String,
      default: '#FFFFFF'
    },
    hideContactInfo: {
      type: Boolean,
      default: false
    },
    minimal: {
      type: Boolean,
      default: false
    },
    socials: {
      type: Boolean,
      default: false
    },
    signingOut: {
      type: Boolean,
      default: false
    },
    signedOut: {
      type: Boolean,
      default: false
    },
  },
  setup(props) {
    props = reactive(props);
    const landingStore = useLandingPageStore();
    const donationSessionStore = useDonationSessionStore();
    const analyticEventStore = useAnalyticEventStore();
    const userStore = useUserStore();
    return {
      donationSessionStore,
      analyticEventStore,
      landingStore,
      userStore,
    };
  },
  data() {
    return {
      userSignOutClicked: false,
    };
  },
  computed: {
    userSignedIn() {
      return !!(this.userStore.user || document.body.dataset.currentUserId);
    },
    ApplicationHelper() {
      return ApplicationHelper;
    },
    computedTextColor() {
      if (this.textColor && this.textColor.trim() !== "") {
        return this.textColor;
      }
      if (this.landingStore && this.landingStore.getContrastTextColor) {
        return this.landingStore.getContrastTextColor(this.backgroundColor);
      }
      return "#000000";
    },
    cssProps() {
      return {
        '--background-color': this.backgroundColor,
        '--text-color': this.computedTextColor,
        '--logo-max-height': this.logoMaxHeight,
        "--color-primary": this.landingStore.localBrand.color_primary,
        "--color-primary-dark": this.landingStore.localBrand.color_primary_dark
      };
    },
    addressLine1() {
      return this.organization?.address || '';
    },
    addressLine2() {
      const parts = [
        this.organization?.city ? this.organization.city + ',' : null,
        this.organization?.state,
        this.organization?.zipcode
      ].filter(Boolean);
      return parts.join(' ');
    },
    phone() {
      return this.organization?.phone_number || '';
    },
    email() {
      return this.organization?.email || '';
    },
    orgName() {
      return this.organization?.name || '';
    },
    hasSocials() {
      return this.socials && (
        this.organization.facebook_url ||
        this.organization.instagram_url ||
        this.organization.youtube_url ||
        this.organization.x_url ||
        this.organization.linkedin_url
      );
    }
  },
  methods: {
    recordAnalyticEvent(action) {
      this.analyticEventStore.setOrganizationId(this.donationSessionStore.organizationId);
      this.analyticEventStore.logEvent({ action });
    },
    handleSignIn(event) {
      event.preventDefault();
      const returnUrl = encodeURIComponent(window.location.href);
      window.location.href = `/users/sign_in?return_to=${returnUrl}`;
      this.analyticEventStore.logEvent({ action: "landing-footer-navigate-to-sign-in" });
    },
    openSignOutModal() {
      this.userSignOutClicked = true;
      this.analyticEventStore.logEvent({ action: "landing-footer-open-sign-out-modal" });
    },
    closeSignOutModal() {
      this.userSignOutClicked = false;
    },
  }
});
</script>

<template>
  <div
    id="landing-page-footer"
    class="outer-container"
    :style="cssProps"
  >
    <div class="inner-container">
      <!-- Minimal footer -->
      <template v-if="minimal">
        <div class="footer-minimal">
          Powered by <a
            href="https://allgive.online"
            target="_blank"
            rel="noopener"
          >AllGive.Online</a>
        </div>
      </template>

      <!-- Full footer -->
      <template v-else>
        <div class="footer-main">
          <!-- Left: Logo + socials -->
          <div class="footer-left">
            <router-link
              v-if="!disableNavigation"
              :to="{ name: 'landing' }"
              class="footer-logo-link"
            >
              <img
                v-if="logoSrc"
                :src="logoSrc"
                :alt="logoAlt"
                class="footer-logo"
                @click="recordAnalyticEvent('landing-footer-navigate-to-landing-home')"
              >
              <span
                v-else
                class="footer-org-name"
              >{{ orgName }}</span>
            </router-link>
            <div
              v-else
              class="footer-logo-link"
            >
              <img
                v-if="logoSrc"
                :src="logoSrc"
                :alt="logoAlt"
                class="footer-logo"
              >
              <span
                v-else
                class="footer-org-name"
              >{{ orgName }}</span>
            </div>

            <div
              v-if="hasSocials"
              class="footer-socials"
            >
              <a
                v-if="organization.facebook_url"
                @click="ApplicationHelper.redirectToExternalUrl(organization.facebook_url, true)"
              >
                <i class="fa-brands fa-facebook" />
              </a>
              <a
                v-if="organization.instagram_url"
                @click="ApplicationHelper.redirectToExternalUrl(organization.instagram_url, true)"
              >
                <i class="fa-brands fa-instagram" />
              </a>
              <a
                v-if="organization.youtube_url"
                @click="ApplicationHelper.redirectToExternalUrl(organization.youtube_url, true)"
              >
                <i class="fa-brands fa-youtube" />
              </a>
              <a
                v-if="organization.x_url"
                @click="ApplicationHelper.redirectToExternalUrl(organization.x_url, true)"
              >
                <i class="fa-brands fa-x-twitter" />
              </a>
              <a
                v-if="organization.linkedin_url"
                @click="ApplicationHelper.redirectToExternalUrl(organization.linkedin_url, true)"
              >
                <i class="fa-brands fa-linkedin" />
              </a>
            </div>
          </div>

          <!-- Right: Contact info -->
          <div
            v-if="layout === 'contact' && !hideContactInfo"
            class="footer-right"
          >
            <p>{{ addressLine1 }}</p>
            <p>{{ addressLine2 }}</p>
            <p v-if="phone">
              <a :href="`tel:${phone.replace(/\D/g,'')}`">{{ phone }}</a>
            </p>
            <p v-if="email">
              <a
                :href="'mailto:' + email"
                @click="recordAnalyticEvent('landing-footer-navigate-to-contact-us')"
              >
                {{ email }}
              </a>
            </p>
          </div>
        </div>

        <!-- Bottom bar: separator + copyright + sign in/out -->
        <div class="footer-bottom">
          <div class="footer-bottom-left">
            <span>Powered by </span>
            <a
              href="https://allgive.online"
              target="_blank"
              rel="noopener"
              @click="recordAnalyticEvent('landing-footer-navigate-to-allgive-home')"
            >
              AllGive.Online
            </a>
          </div>
          <div class="footer-bottom-right">
            <a
              v-if="!userSignedIn"
              href="/users/sign_in"
              @click="handleSignIn"
            >Sign In</a>
            <span
              v-else
              class="sign-out-link"
              @click="openSignOutModal"
            >Sign Out</span>
          </div>
        </div>
      </template>
    </div>

    <base-modal
      v-if="userSignOutClicked"
      title="Sign Out?"
      :background-dismissible="false"
      @close="closeSignOutModal"
    >
      <template #default>
        <p>Are you sure you want to sign out?</p>
        <div class="modal-actions">
          <button
            class="btn btn-sign-out"
            @click="userStore.signOut()"
          >
            Sign Out
          </button>
          <button
            class="btn btn-cancel"
            @click="closeSignOutModal"
          >
            Cancel
          </button>
        </div>
      </template>
    </base-modal>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';

#landing-page-footer {
  background-color: var(--background-color);
  color: var(--text-color);

  .inner-container {
    padding-top: $space-3xl;
    padding-bottom: $space-2xl;
  }

  a {
    color: var(--text-color);
    text-decoration: none;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.7;
    }
  }

  .footer-logo {
    max-height: var(--logo-max-height);
    max-width: 100%;
  }

  .footer-logo-link {
    display: inline-block;
  }

  .footer-org-name {
    font-size: $section-title-size;
    font-weight: $section-title-weight;
  }

  // Main content: logo left, contact right
  .footer-main {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: $space-2xl;
  }

  .footer-left {
    display: flex;
    flex-direction: column;
    gap: $space-xl;
  }

  .footer-right {
    text-align: right;

    p {
      margin: 0 0 2px 0;
      font-size: 14px;
      line-height: 20px;
      opacity: 0.9;
    }

    a {
      font-weight: 500;
      text-decoration: underline;
    }
  }

  // Social icons
  .footer-socials {
    display: flex;
    gap: $space-lg;

    a {
      font-size: 1.5rem;
      cursor: pointer;
    }
  }

  // Bottom bar with separator
  .footer-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: $space-2xl;
    padding-top: $space-lg;
    border-top: 1px solid currentColor;
    opacity: 0.8;
    font-size: 12px;

    a {
      &:hover {
        opacity: 1;
      }
    }
  }

  .footer-bottom-right {
    font-size: 11px;
  }

  .sign-out-link {
    cursor: pointer;
    text-decoration: underline;
  }

  // Minimal footer
  .footer-minimal {
    text-align: center;
    font-size: $section-body-size;
    padding: $space-lg 0;
  }

  // Modal actions
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: $space-sm;
    margin-top: $space-lg;
  }

  .btn-sign-out {
    background-color: var(--color-primary);
    color: white;

    &:hover {
      background-color: var(--color-primary-dark);
    }
  }

  .btn-cancel {
    border: 1px solid var(--text-color);
    background: transparent;

    &:hover {
      background-color: var(--color-primary-dark);
      color: white;
    }
  }
}

// Mobile responsive
@media screen and (max-width: $breakpoint-max-md) {
  #landing-page-footer {
    .footer-main {
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .footer-left {
      align-items: center;
    }

    .footer-right {
      text-align: center;
    }

    .footer-socials {
      justify-content: center;
    }

    .footer-bottom {
      flex-direction: column;
      gap: $space-sm;
      text-align: center;
    }
  }
}

// Container responsiveness - builder preview
@container (min-width: 0px) and (max-width: 639px) {
  #landing-page-footer {
    .footer-main {
      flex-direction: column !important;
      align-items: center !important;
      text-align: center !important;
    }

    .footer-left {
      align-items: center !important;
    }

    .footer-right {
      text-align: center !important;
    }

    .footer-socials {
      justify-content: center !important;
    }

    .footer-bottom {
      flex-direction: column !important;
      gap: $space-sm !important;
      text-align: center !important;
    }
  }
}
</style>
